// src/components/PostCard.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { normalizeMediaUrl } from "../lib/media";
import { registerClipView } from "../lib/registerClipView";

function visibilityLabel(v) {
  return {
    public: "🌍 Public",
    friends: "👥 Friends",
    private: "🔒 Only me",
  }[v] || "🌍 Public";
}

function renderMedia(raw, onOpen, detailView) {
  const url = normalizeMediaUrl(raw);
  if (!url) return null;

  const u = url.toLowerCase();

  if (u.match(/\.(png|jpe?g|gif|webp|avif|bmp)$/)) {
    return (
      <img
  src={url}
  alt=""
  className="media-img"
  onClick={onOpen}
  style={{
    cursor: "zoom-in",
    ...(detailView
      ? {
          maxWidth: "100%",
          maxHeight: "35vh",
          objectFit: "contain",
        }
      : {}),
  }}
/>
    );
  }

  if (u.match(/\.(mp4|webm|ogg)$/)) {
    return (
      <video
  src={url}
  controls
  className="media-vid"
  style={{
    ...(detailView
      ? {
          maxWidth: "100%",
          maxHeight: "35vh",
          objectFit: "contain",
        }
      : {}),
  }}
/>
    );
  }

  if (u.match(/\.(mp3|wav|m4a)$/)) {
    return <audio src={url} controls className="media-aud" />;
  }

  return null;
}

export default function PostCard({
  p,
  detailView = false,
  onViewCount,
  isOwnProfile,
  onSupport,
  onRepost,
  onShare,
  onDelete,
  onDeleteRepost,
  onChangePrivacy,
  onPrivacyChanged,
}) {
  
  const localCommentCount =
  p.comment_count ?? p.comments_count ?? 0;

  const [showPrivacySelect, setShowPrivacySelect] = useState(false);
  const [currentVisibility, setCurrentVisibility] = useState(
    p.visibility || "public"
  );
  
  const navigate = useNavigate();

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  useEffect(() => {
    setCurrentVisibility(p.visibility || "public");
  }, [p.visibility]);

  const avatar =
    normalizeMediaUrl(p.profile_pic_url) || "/uploads/default-avatar.png";

  const media = Array.isArray(p.media_urls)
    ? p.media_urls
    : (() => {
        try {
          return JSON.parse(p.media_urls || "[]");
        } catch {
          return [];
        }
      })();

  const supportCount = p.support_count ?? 0;
  const repostCount = p.repost_count ?? p.reposts_count ?? 0;

  const openViewer = (index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
  };

  const showPrev = () => {
    setViewerIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const showNext = () => {
    setViewerIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  const activeMedia = media[viewerIndex];
  const activeUrl = normalizeMediaUrl(activeMedia);

  const isActiveImage = activeUrl?.match(
    /\.(png|jpe?g|gif|webp|avif|bmp)(\?.*)?$/i
  );

  const isActiveVideo = activeUrl?.match(
    /\.(mp4|webm|ogg)(\?.*)?$/i
  );
  
  const isClip =
  p.type === "clip" ||
  p.post_type === "clip" ||
  p.is_clip === true;
  
  
  const openDetail = () => {
  if (detailView) return;

  if (isClip) {
    navigate(`/clips/${p.id}`);
  } else {
    navigate(`/post/${p.id}`);
  }
};



  return (
    <article className="post-card">
      {/* HEADER */}
      <div
  className="post-head"
  onClick={openDetail}
  style={{
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
  }}
>
        <Link   to={`/u/${p.user_id}`}   onClick={(e) => e.stopPropagation()} >
          <img
            src={avatar}
            alt=""
            style={{ width: 40, height: 40, borderRadius: "50%" }}
          />
        </Link>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Link
  to={`/u/${p.user_id}`}
  onClick={(e) => e.stopPropagation()}
  style={{
    display: "flex",
    alignItems: "center",
    gap: 6,
    textDecoration: "none",
  }}
>
            <span style={{ fontWeight: 600, color: "#e5e7eb" }}>
              {p.username || p.user_id}
            </span>

            <span style={{ color: "#9ca3af", fontSize: 14 }}>
              @{p.user_id}
            </span>
          </Link>

          {p._displayDate && (
            <span style={{ color: "#9ca3af", fontSize: 13 }}>
              · {p._displayDate}
            </span>
          )}
        </div>
      </div>

      {/* REPOST LABEL */}
      {p.is_repost && isOwnProfile && (
        <div className="repost-label">🔁 You reposted this</div>
      )}

      {/* OWNER ACTIONS */}
      {isOwnProfile && (
        <div
          style={{
            display: "flex",
            gap: 12,
            marginLeft: 56,
            marginBottom: 6,
            fontSize: 13,
          }}
        >
          {!p.is_repost && (
            <button
              className="link-btn"
              onClick={() => setShowPrivacySelect((v) => !v)}
            >
              {visibilityLabel(currentVisibility)}
            </button>
          )}

          {!p.is_repost && (
            <button className="link-btn danger" onClick={() => onDelete(p)}>
              🗑 Delete
            </button>
          )}

          {p.is_repost && (
            <button
              className="link-btn danger"
              onClick={() => onDeleteRepost(p)}
            >
              🗑 Delete repost
            </button>
          )}
        </div>
      )}

      {/* FOOTPRINT */}
{p.location_id && (
  <div
    onClick={(e) => {
  e.stopPropagation();
  navigate(`/location/${p.location_id}`);
}}
    style={{
      marginLeft: 56,
      marginTop: 6,
      marginBottom: 8,
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: 999,
      background: "#122033",
      color: "#60a5fa",
      fontSize: 13,
      cursor: "pointer"
    }}
  >
    📍 {p.location_name || "Location"}
  </div>
)}

{/* TEXT */}
{p.text && (
  <div
    className="post-text"
    onClick={openDetail}
    style={{ cursor: "pointer" }}
  >
    {p.text}
  </div>
)}

      {/* HASHTAG */}
      {p.hashtag && (
        <div
          className="post-hashtag"
          style={{
            marginTop: 6,
            color: "#60a5fa",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          #{p.hashtag}
        </div>
      )}

      {/* MEDIA */}
      {media.length > 0 && (
  <div
  className="media-grid"
  style={
    detailView
      ? {
          display: "flex",
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          gap: 8,
          maxHeight: "35vh",
        }
      : undefined
  }
>
    {media.map((m, i) => {
      const url = normalizeMediaUrl(m);
      const lower = (url || "").toLowerCase();
      const isVideo = lower.match(/\.(mp4|webm|ogg)$/);

      if (isClip && isVideo) {
          return (
  <div
    key={i}
    style={
      detailView
        ? {
            minWidth: "100%",
            position: "relative",
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            scrollSnapAlign: "center",
          }
        : {
            position: "relative",
            cursor: "pointer",
          }
    }
            onClick={() => navigate(`/clips?clip=${p.id}`)}
          >
            <video
  ref={(video) => {
    if (!video) return;

    registerClipView(video, p.id, (count) => {
      onViewCount?.(count);
    });
  }}
  src={url}
  className="media-vid"
  muted
  playsInline
  preload="metadata"
  style={{
    pointerEvents: "none",
    ...(detailView
      ? {
          maxWidth: "100%",
          maxHeight: "35vh",
          objectFit: "contain",
        }
      : {}),
  }}
/>
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(to top, rgba(0,0,0,.35), rgba(0,0,0,.08))"
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "rgba(0,0,0,0.55)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 22
                }}
              >
                ▶
              </div>
            </div>
          </div>
        );
      }

      return (   <div
  key={i}
  style={
    detailView
      ? {
          minWidth: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          scrollSnapAlign: "center",
        }
      : undefined
  }
>
  {renderMedia(m, () => openViewer(i), detailView)}
</div>
);
      })}
    </div>
)}

{typeof p.view_count === "number" && (
  <div
    style={{
      marginTop: 8,
      marginBottom: 6,
      color: "#9ca3af",
      fontSize: 13,
      paddingLeft: 4,
    }}
  >
    👁 {p.view_count} {p.view_count === 1 ? "view" : "views"}
  </div>
)}

      {/* ACTIONS */}
<div className="actions post-actions">
  <button
    className={`v-action support ${p.is_supported ? "active" : ""}`}
    onClick={() => onSupport(p)}
  >
    <span className="icon support-icon">↑</span>
    <span className="label">{supportCount}</span>
  </button>

  <button
    className="v-action repost"
    onClick={() => onRepost(p.id)}
  >
    <span className="icon">⟳</span>
    <span className="label">{repostCount}</span>
  </button>

  <button
    className="v-action comment"
    onClick={openDetail}
  >
    <span className="icon">💬</span>
    <span className="label">{localCommentCount}</span>
  </button>

  <button
    className="v-action"
    onClick={() => onShare(p.id)}
  >
    <span className="icon">↗</span>
  </button>

  <button
    className="v-action"
    onClick={() => {
      const me = JSON.parse(localStorage.getItem("user") || "{}");

      api
        .post("/reports", {
          reporterId: me.id,
          targetType: "post",
          targetId: p.id,
          reason: "spam",
        })
        .then(() => alert("Report submitted"))
        .catch(() => alert("Report failed"));
    }}
  >
    <span className="icon">🚩</span>
  </button>
</div>

      {isOwnProfile && !p.is_repost && showPrivacySelect && (
        <select
          value={currentVisibility}
          onChange={(e) => {
            const next = e.target.value;

            setCurrentVisibility(next);
            onChangePrivacy(p, next);

            if (onPrivacyChanged) {
              onPrivacyChanged(p.id, next);
            }

            setShowPrivacySelect(false);
          }}
          style={{
            marginLeft: 56,
            marginBottom: 8,
            background: "#0e131a",
            color: "#e5e7eb",
            border: "1px solid #1f2937",
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 13,
          }}
        >
          <option value="public">🌍 Public</option>
          <option value="friends">👥 Friends</option>
          <option value="private">🔒 Only me</option>
        </select>
      )}

      {/* MEDIA VIEWER */}
      {viewerOpen && (
        <div className="media-viewer-overlay" onClick={closeViewer}>
          <button
  className="media-viewer-close"
  onClick={(e) => {
    e.stopPropagation();
    closeViewer();
  }}
  type="button"
>
  ✕
</button>

          {media.length > 1 && (
            <>
              <button
                className="media-viewer-nav media-viewer-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  showPrev();
                }}
                type="button"
              >
                ‹
              </button>

              <button
                className="media-viewer-nav media-viewer-next"
                onClick={(e) => {
                  e.stopPropagation();
                  showNext();
                }}
                type="button"
              >
                ›
              </button>
            </>
          )}

          <div
            className="media-viewer-content"
            onClick={(e) => e.stopPropagation()}
          >
            {isActiveImage && (
              <img src={activeUrl} alt="" className="media-viewer-img" />
            )}

            {isActiveVideo && (
  <video
    ref={(video) => {
      if (!video) return;

      if (isClip) {
  registerClipView(video, p.id, (count) => {
    onViewCount?.(count);
  });
}
    }}
    src={activeUrl}
    controls
    autoPlay
    className="media-viewer-video"
  />
)}
          </div>
        </div>
      )}

      
    </article>
  );
}